import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Box, Flex, Grid, Spinner } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import SelectInput from "components/forms/SelectInput";
import { mnt, mntCard, mntType, mntPill, mntStatCard } from "theme/maintenanceTokens";
import type { MntStatus } from "theme/maintenanceTokens";

import {
  getServicePlan,
  getPlanStats,
  getPlanEnrichedSitesPage,
  getWorkOrders,
  getPlanLocationMap,
} from "api/ServicePlans";
import { lifecycleBadge, workOrderDisplayIdentity } from "./components/workOrderPresentation";
import RequestWorkModal from "./components/RequestWorkModal";
import PlanLocationsMap, { PlanMapPoint } from "./components/PlanLocationsMap";

const PAGE_SIZE = 10;
const WO_PAGE_SIZE = 10;

type ClientPlanTab = "work" | "locations";

const CLIENT_PLAN_TABS: Array<{ key: ClientPlanTab; label: string }> = [
  { key: "work", label: "Work orders" },
  { key: "locations", label: "Locations" },
];

/**
 * CLIENT view of a maintenance plan (Session 62, mockup 10) — a lean,
 * read-only plan page on the SAME route as the operator PlanDetail,
 * gated by role in that component. The operator console (batches,
 * roster, config, new-WO/quote modals, close-plan) is never mounted for
 * a client.
 *
 * Reads the already-client-safe plan endpoints (server-scoped +
 * pricing-stripped for non-company requesters): the plan, its stats, and
 * its enriched sites. Shows the client vocabulary — sites in plan,
 * completed, in flight, yards YTD — and a sites table with client-safe
 * status. A site row links to that site's job status
 * (/maintenance/work-orders/:id) when the enriched row carries a work
 * order id. Work-order-fed (Corrigo) plans show the Corrigo notice.
 */
export default function PlanClientDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [plan, setPlan] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [loadingHead, setLoadingHead] = useState(true);

  // Server-paged sites (mirrors the operator roster: the paged endpoint
  // applies search + status + paging in SQL and returns the SAME per-row
  // status signals the operator reads, so the client status can't drift
  // from the internal one). At 125+ sites a client-side dump + filter is
  // the wrong shape here exactly as it is for staff.
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Work Orders table (SESSION 62) — a client-safe WO list ABOVE the
  // Locations table, filtered by a client-meaningful status axis. The
  // Locations table answers "what sites are in my plan"; this answers
  // "what is the status of the jobs at those sites". Server-scoped to
  // this plan + the client's org and field-stripped by the backend.
  const [woRows, setWoRows] = useState<any[]>([]);

  /**
   * KNOWN_ISSUES item 18 — client self-service. The button only exists
   * when the plan carries `clientSelfServiceEnabled`, which is a
   * PER-PLAN opt-in we control. Hiding it is a courtesy, not the
   * control: the endpoint re-checks the same flag server-side, so a
   * client who finds the route by hand still gets a 403.
   */
  const [requestOpen, setRequestOpen] = useState(false);
  // Preset site for the modal when the request is raised from a specific
  // row rather than the section header. null = the header's generic
  // "Request work", which opens with the site picker.
  const [requestPreset, setRequestPreset] = useState<any>(null);
  /** "request" = come and look; "order" = I know the quantity, price it. */
  const [requestMode, setRequestMode] = useState<"request" | "order">("request");

  /** Open the modal for one site in one intent. */
  const openRequest = (
    site: any,
    nextMode: "request" | "order",
  ) => {
    setRequestPreset(site);
    setRequestMode(nextMode);
    setRequestOpen(true);
  };

  /**
   * TABS on the client page (Session 76, Jack's call).
   *
   * Lighter than the operator page's four — a client has two sections,
   * not ten. The argument for tabs here is not page length, it is that
   * "what is happening with my jobs" and "what sites am I covered for"
   * are two different questions, and a client visiting occasionally
   * should not have to work out which table answers theirs.
   *
   * The stat cards stay ABOVE the tabs for the same reason they do on
   * the operator page: they are the plan, not one view of it.
   */
  const [tab, setTab] = useState<ClientPlanTab>(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t === "locations" ? "locations" : "work";
  });
  const selectTab = (next: ClientPlanTab) => {
    setTab(next);
    const params = new URLSearchParams(window.location.search);
    if (next === "work") params.delete("tab");
    else params.set("tab", next);
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? `?${qs}` : ""),
    );
  };

  /**
   * Map, on the Locations tab. The payload was checked against the
   * client data-safety rule before exposing it: getPlanLocationMap
   * returns name, address, coordinates, batch tag, map status and open
   * WO count — no provider, no cost, no margin, no pricing. The endpoint
   * gate is `assertPlanReadable`, the same one this page's other reads
   * already pass.
   *
   * Lazy: fetched the first time the Locations tab is opened, cached
   * after. A client who only ever looks at work orders never pays for
   * it.
   */
  const [mapPoints, setMapPoints] = useState<PlanMapPoint[] | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [selectedMapLocationId, setSelectedMapLocationId] = useState<string | null>(null);
  const [woTotal, setWoTotal] = useState(0);
  const [woPage, setWoPage] = useState(1);
  const [woLoading, setWoLoading] = useState(true);
  // Client-meaningful buckets (Jack): Open / In progress / Completed /
  // All. "Completed" = the WO's project is Complete (stepNumber >= 5),
  // a real backend lifecycle bucket, not the operator caseClosed flag.
  const [woStatus, setWoStatus] = useState("open");
  // Session 69 — search on the client WO table, mirroring the Locations
  // search directly below it (same debounce, same input styling). Goes
  // through the backend's new `search` param rather than filtering in the
  // browser, because this table is SERVER-PAGED: a client-side filter
  // would only ever search the 10 rows on the current page.
  const [woSearchInput, setWoSearchInput] = useState("");
  const [woSearch, setWoSearch] = useState("");

  // Header (plan + stats) — once.
  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Plan";
    if (!id) return;
    let alive = true;
    setLoadingHead(true);
    Promise.all([
      getServicePlan({ data: { id } }),
      getPlanStats({ data: { id } }),
    ])
      .then(([planRes, statsRes]) => {
        if (!alive) return;
        setPlan(planRes?.id ? planRes : null);
        setStats(statsRes || null);
        setLoadingHead(false);
      })
      .catch(() => {
        if (alive) setLoadingHead(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  // Debounce the search box: each keystroke is a query at scale.
  const searchTimer = useRef<any>(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  // The client status filter maps to the paged endpoint's own axes:
  // overdue/scheduled(due_soon)/current are the cadence axis; "quoted"
  // (a WO is open) is the has-open-WO axis. Sent as cadence/status params
  // so filtering happens in SQL, not over one page.
  const sitesParams = useMemo(() => {
    switch (statusFilter) {
      case "overdue":
        return { cadence: "overdue" };
      case "scheduled":
        return { cadence: "due_soon" };
      case "current":
        return { cadence: "current" };
      case "quoted":
        return { status: "wo_received" };
      // "Nothing raised yet" — the only rows that carry a Request work
      // action, and the set a client actually needs to find. Server value
      // is `dormant`: site attached to the plan, no work order, never
      // serviced. Without this the rows are unreachable except by paging,
      // because sites with work orders sort first.
      case "none":
        return { status: "dormant" };
      default:
        return {};
    }
  }, [statusFilter]);

  const loadSites = useCallback(async () => {
    if (!id) return;
    setLoadingSites(true);
    const res: any = await getPlanEnrichedSitesPage({
      data: {
        id,
        page,
        pageSize: PAGE_SIZE,
        search: search || null,
        // Float every site that has a work order to the top — open OR
        // converted — since those are the only rows a client can act on or
        // click into. Use has_wo (WO id exists), NOT open_wo: open_wo
        // counts only received/matched WOs, so a converted "In progress"
        // site (openWoCount 0) would sink to the bottom with the empty
        // ones. At 125 sites the handful with a job must lead.
        sort: "has_wo",
        sortDir: "DESC",
        ...sitesParams,
      },
    });
    setRows(Array.isArray(res?.items) ? res.items : []);
    setTotal(Number(res?.total ?? 0));
    setLoadError(!!res?.error);
    setLoadingSites(false);
  }, [id, page, search, sitesParams]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Work Orders fetch — plan-scoped, paged, filtered by the
  // client-meaningful lifecycle. The backend scopes to the client's org
  // and strips operator-only fields; here we just render the lean row.
  // "all" sends no lifecycle so the backend returns every WO on the plan.
  const loadWorkOrders = useCallback(async () => {
    if (!id) return;
    setWoLoading(true);
    const query: any = { servicePlanId: id, page: woPage, pageSize: WO_PAGE_SIZE };
    if (woStatus && woStatus !== "all") query.lifecycle = woStatus;
    if (woSearch) query.search = woSearch;
    const res: any = await getWorkOrders({ query });
    // The list endpoint returns a { items, total, ... } envelope when
    // paged, a bare array otherwise. Handle both.
    if (Array.isArray(res)) {
      setWoRows(res);
      setWoTotal(res.length);
    } else {
      setWoRows(Array.isArray(res?.items) ? res.items : []);
      setWoTotal(Number(res?.total) || 0);
    }
    setWoLoading(false);
  }, [id, woPage, woStatus, woSearch]);

  useEffect(() => {
    loadWorkOrders();
  }, [loadWorkOrders]);

  // A status change restarts WO paging at 1 so we never land past the
  // end of a freshly-narrowed set.
  useEffect(() => {
    setWoPage(1);
  }, [woStatus]);

  // Lazy map read — fires the first time the Locations tab is opened
  // with the map expanded, and is cached in state afterwards.
  useEffect(() => {
    if (tab !== "locations" || !showMap || mapPoints !== null) return;
    let cancelled = false;
    setMapLoading(true);
    getPlanLocationMap({ data: { id } })
      .then((rows: any[]) => {
        if (!cancelled) setMapPoints(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setMapPoints([]);
      })
      .finally(() => {
        if (!cancelled) setMapLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, showMap, mapPoints, id]);

  // Debounce the WO search box, same 350ms as the Locations one. Resets
  // paging so a search never lands past the end of a narrowed set.
  const woSearchTimer = useRef<any>(null);
  useEffect(() => {
    if (woSearchTimer.current) clearTimeout(woSearchTimer.current);
    woSearchTimer.current = setTimeout(() => {
      setWoSearch(woSearchInput.trim());
      setWoPage(1);
    }, 350);
    return () => {
      if (woSearchTimer.current) clearTimeout(woSearchTimer.current);
    };
  }, [woSearchInput]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (loadingHead) {
    return (
      <>
        <Header></Header>
        <Page title="Plan">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  if (!plan) {
    return (
      <>
        <Header></Header>
        <Page title="Plan">
          <Box sx={{ ...mntCard, padding: "24px", textAlign: "center", maxWidth: "600px", marginX: "auto" }}>
            <Subtext fontSize={"14px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
              This plan could not be found on your account.
            </Subtext>
          </Box>
        </Page>
      </>
    );
  }

  const corrigoManaged = plan.intakeStyle === "work_order_fed";
  const committed = plan.committedSiteCount ?? null;
  const term =
    plan.startDate || plan.endDate
      ? [fmtDate(plan.startDate), fmtDate(plan.endDate)].filter(Boolean).join(" - ")
      : "Ongoing";
  const sitesInPlan = stats?.sitesInPlan ?? total;

  return (
    <>
      <Header></Header>
      <Page title={plan.name || "Maintenance plan"} $noPadding={true}>
        <Box sx={{ maxWidth: "1200px", marginX: "auto", width: "100%", paddingBottom: "40px" }}>
          {/* Header */}
          <Box sx={{ marginTop: "16px", marginBottom: "18px" }}>
            <button
              type="button"
              onClick={() => navigate("/maintenance")}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                fontFamily: "Poppins",
                fontSize: "11px",
                color: mnt.link,
                cursor: "pointer",
                marginBottom: "8px",
              }}
            >
              {"\u2039"} Maintenance
            </button>
            <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"22px"}>
              {plan.name || "Maintenance plan"}
            </H5>
            <Subtext fontSize={"13px"} fontFamily={"Poppins"} marginTop={"5px"} marginBottom={0} color={mnt.textMuted}>
              {term}
            </Subtext>
          </Box>

          {/* Stat cards */}
          <Grid columns={[2, 4]} gap={"12px"} sx={{ marginBottom: "20px" }}>
            <StatCard
              label="Sites in plan"
              value={sitesInPlan}
              sub={committed != null ? `${committed} committed` : "Ongoing"}
            />
            <StatCard
              label="Completed"
              value={stats?.sitesServiced ?? 0}
              sub={committed != null && committed > 0 ? `${stats?.sitesServiced ?? 0} of ${committed}` : "All-time"}
            />
            <StatCard
              label="Yards YTD"
              value={formatYards(stats?.yardsYtd ?? 0)}
              sub={"\u00A0"}
            />
            <StatCard
              label="Sites serviced"
              value={stats?.sitesServiced ?? 0}
              sub={
                sitesInPlan > 0
                  ? `${Math.round(((stats?.sitesServiced ?? 0) / Math.max(sitesInPlan, 1)) * 100)}% of plan`
                  : "\u00A0"
              }
            />
          </Grid>

          {/* Tab bar. Stat cards above it — they are the plan, not one
              view of it. Two tabs only: a client has two questions. */}
          <Flex
            sx={{
              gap: "2px",
              borderBottom: `1px solid ${mnt.divider}`,
              marginBottom: "20px",
              overflowX: "auto",
            }}
          >
            {CLIENT_PLAN_TABS.map((t) => {
              const on = t.key === tab;
              const n = t.key === "work" ? woTotal : total;
              return (
                <Box
                  as="button"
                  key={t.key}
                  onClick={() => selectTab(t.key)}
                  sx={{
                    background: "transparent",
                    border: "none",
                    borderBottom: on ? `2px solid ${mnt.ink}` : "2px solid transparent",
                    padding: "9px 16px",
                    marginBottom: "-1px",
                    cursor: "pointer",
                    fontFamily: "Poppins",
                    fontSize: "13px",
                    fontWeight: on ? 600 : 400,
                    color: on ? mnt.ink : mnt.textMuted,
                    whiteSpace: "nowrap",
                    "&:hover": { color: mnt.ink },
                  }}
                >
                  {t.label}
                  {n > 0 && (
                    <Box
                      as="span"
                      sx={{
                        marginLeft: "7px",
                        fontFamily: "Poppins",
                        fontSize: "11px",
                        color: on ? mnt.ink : mnt.textFaint,
                      }}
                    >
                      {n.toLocaleString()}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Flex>

          {/* Work Orders table (SESSION 62). Answers
              "what is the status of jobs at my sites", separate from the
              Locations roster on the other tab. */}
          {tab === "work" && (
          <Box sx={{ marginBottom: "22px" }}>
            <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
              <H5 fontFamily="Poppins" fontWeight={600} margin={0} fontSize={"15px"}>
                Work orders
              </H5>
              <Flex sx={{ gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                {/* Item 18 — the ONE button. The mockup is explicit that
                    the plan page is a reading surface with a single
                    action: no separate "add a location" and no separate
                    "request a price", because to a client those are the
                    same thing ("I need something done at a site"). */}
                {plan?.clientSelfServiceEnabled && (
                  <Flex sx={{ gap: "8px" }}>
                    <button
                      type="button"
                      onClick={() => openRequest(null, "request")}
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "7px 14px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        background: "transparent",
                        border: "1px solid " + mnt.divider,
                        color: mnt.ink,
                      }}
                    >
                      Request work
                    </button>
                    <button
                      type="button"
                      onClick={() => openRequest(null, "order")}
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "7px 14px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        background: mnt.link,
                        border: "1px solid " + mnt.link,
                        color: "#fff",
                      }}
                    >
                      Create work order
                    </button>
                  </Flex>
                )}
                <Box sx={{ minWidth: "180px" }}>
                  <input
                    type="text"
                    value={woSearchInput}
                    onChange={(e: any) => setWoSearchInput(e.target.value)}
                    placeholder="Search work orders"
                    style={{
                      width: "100%",
                      padding: "7px 10px",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      color: mnt.ink,
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      outline: "none",
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: "170px" }}>
                  <SelectInput
                    name="woStatus"
                    value={woStatus}
                    options={[
                      { value: "open", label: "Open" },
                      { value: "in_progress", label: "In progress" },
                      { value: "completed", label: "Completed" },
                      { value: "all", label: "All" },
                    ]}
                    onChange={(v: any) => setWoStatus(v ?? "open")}
                    searchable={false}
                    clearable={false}
                  />
                </Box>
              </Flex>
            </Flex>

            <Box sx={{ ...mntCard, overflow: "hidden" }}>
              <Flex
                sx={{
                  padding: "10px 16px",
                  background: mnt.bgSubtle,
                  borderBottom: "0.5px solid " + mnt.divider,
                  gap: "10px",
                }}
              >
                <Box sx={{ flex: "0 0 24%", ...mntType.labelSm, fontWeight: 600 }}>WORK ORDER</Box>
                <Box sx={{ flex: "0 0 30%", ...mntType.labelSm, fontWeight: 600 }}>LOCATION</Box>
                <Box sx={{ flex: "0 0 22%", ...mntType.labelSm, fontWeight: 600 }}>SERVICE</Box>
                <Box sx={{ flex: "0 0 24%", ...mntType.labelSm, fontWeight: 600 }}>STATUS</Box>
              </Flex>

              {woLoading ? (
                <Box sx={{ textAlign: "center", paddingY: "36px" }}>
                  <Spinner />
                </Box>
              ) : woRows.length === 0 ? (
                <Box sx={{ padding: "34px", textAlign: "center" }}>
                  <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                    {woStatus !== "all" ? "No work orders match this view." : "No work orders on this plan yet."}
                  </Subtext>
                </Box>
              ) : (
                woRows.map((wo, idx) => {
                  const badge = lifecycleBadge(wo);
                  const identity = workOrderDisplayIdentity(wo);
                  const locName =
                    wo.lcp?.name || wo.lcp?.location?.name || wo.lcp?.location?.address?.line1 || "-";
                  /* Street line under the site label, for the same reason
                     as the Locations table: "Learning Care - 4" is our
                     record name, not how a client knows the playground.
                     Suppressed when locName already fell back to it. */
                  const woAddr: any = wo.lcp?.location?.address;
                  const woLine1 =
                    typeof woAddr === "string" ? woAddr : woAddr?.line1 || "";
                  const woCity = typeof woAddr === "string" ? "" : woAddr?.city || "";
                  const woAddrLine =
                    woLine1 && woLine1 !== locName
                      ? [woLine1, woCity].filter(Boolean).join(", ")
                      : "";
                  const service = wo.service?.name || "-";
                  return (
                    <Flex
                      key={wo.id || idx}
                      onClick={() => navigate(`/maintenance/work-orders/${wo.id}`)}
                      sx={{
                        alignItems: "center",
                        gap: "10px",
                        padding: "11px 16px",
                        cursor: "pointer",
                        borderBottom: idx === woRows.length - 1 ? "none" : "0.5px solid " + mnt.divider,
                        "&:hover": { background: mnt.bgSubtle },
                      }}
                    >
                      <Box sx={{ flex: "0 0 24%", minWidth: 0 }}>
                        <Span fontFamily={"Poppins"} fontSize={"12.5px"} sx={{ color: mnt.link, fontWeight: 500 }}>
                          {identity.primary}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 30%", minWidth: 0 }}>
                        <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.ink}>
                          {locName}
                        </Span>
                        {woAddrLine && (
                          <Box
                            as="div"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              color: mnt.textMuted,
                              marginTop: "2px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {woAddrLine}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: "0 0 22%", minWidth: 0 }}>
                        <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                          {service}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 24%", minWidth: 0 }}>
                        <Badge as="span" variant={badge.variant as any}>
                          {badge.label}
                        </Badge>
                      </Box>
                    </Flex>
                  );
                })
              )}
            </Box>

            {/* WO pagination */}
            {woTotal > WO_PAGE_SIZE && (
              <Flex sx={{ marginTop: "10px", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                  {`${(woPage - 1) * WO_PAGE_SIZE + 1}\u2013${Math.min(woPage * WO_PAGE_SIZE, woTotal)} of ${woTotal}`}
                </Subtext>
                <PageButton label={"\u2039"} disabled={woPage <= 1} onClick={() => setWoPage(woPage - 1)} />
                <PageButton
                  label={"\u203A"}
                  disabled={woPage * WO_PAGE_SIZE >= woTotal}
                  onClick={() => setWoPage(woPage + 1)}
                />
              </Flex>
            )}
          </Box>
          )}

          {/* Locations tab — map above the roster it plots. */}
          {tab === "locations" && (
          <>
          <Box sx={{ marginBottom: "14px" }}>
            <Flex
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: showMap ? "10px" : 0,
              }}
            >
              <Box sx={{ ...mntType.labelSm, textTransform: "uppercase" }}>Map</Box>
              <Box
                as="button"
                onClick={() => setShowMap((v) => !v)}
                sx={{
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: mnt.link,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {showMap ? "Hide map" : "Show map"}
              </Box>
            </Flex>
            {showMap &&
              (mapLoading && mapPoints === null ? (
                <Box
                  sx={{
                    height: "380px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: mnt.bgSubtle,
                    borderRadius: "8px",
                    color: mnt.textMuted,
                    fontFamily: "Poppins",
                    fontSize: "13px",
                  }}
                >
                  Loading map…
                </Box>
              ) : (
                <PlanLocationsMap
                  points={mapPoints || []}
                  selectedId={selectedMapLocationId}
                  onSelect={setSelectedMapLocationId}
                  onOpenLocation={(locId: string) => navigate(`/locations/${locId}`)}
                />
              ))}
          </Box>

          {/* Locations table */}
          <Box sx={{ marginBottom: "10px" }}>
            <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
              <H5 fontFamily="Poppins" fontWeight={600} margin={0} fontSize={"15px"}>
                Locations
              </H5>
              <Flex sx={{ gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                <Box sx={{ minWidth: "180px" }}>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search locations"
                    style={{
                      width: "100%",
                      padding: "7px 10px",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      color: mnt.ink,
                      border: "0.5px solid " + mnt.divider,
                      borderRadius: "4px",
                      outline: "none",
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: "150px" }}>
                  <SelectInput
                    name="status"
                    value={statusFilter}
                    options={[
                      { value: "", label: "All statuses" },
                      { value: "none", label: "Nothing raised yet" },
                      { value: "current", label: "Current" },
                      { value: "scheduled", label: "Due soon" },
                      { value: "quoted", label: "Work order open" },
                      { value: "overdue", label: "Overdue" },
                    ]}
                    onChange={(v: any) => {
                      setStatusFilter(v ?? "");
                      setPage(1);
                    }}
                    searchable={false}
                    clearable={false}
                  />
                </Box>
              </Flex>
            </Flex>

            <Box sx={{ ...mntCard, overflow: "hidden" }}>
              <Flex
                sx={{
                  padding: "10px 16px",
                  background: mnt.bgSubtle,
                  borderBottom: "0.5px solid " + mnt.divider,
                  gap: "10px",
                }}
              >
                {/* Widths sum to 90%, not 100%. The row is a flex with
                    `gap: 10px` between five cells — 40px of gap on top of a
                    100% basis overflows the container and pushes the
                    action column past the right edge, which is why the
                    button rendered clipped mid-word. Widening the action
                    column alone could not fix it; the total had to come
                    down. Session 76. */}
                <Box sx={{ flex: "0 0 24%", ...mntType.labelSm, fontWeight: 600 }}>LOCATION</Box>
                <Box sx={{ flex: "0 0 14%", ...mntType.labelSm, fontWeight: 600 }}>CITY</Box>
                <Box sx={{ flex: "0 0 13%", ...mntType.labelSm, fontWeight: 600 }}>LAST SERVICE</Box>
                <Box sx={{ flex: "0 0 14%", ...mntType.labelSm, fontWeight: 600 }}>STATUS</Box>
                <Box sx={{ flex: "1 1 auto", minWidth: "190px" }} />
              </Flex>

              {loadingSites ? (
                <Box sx={{ textAlign: "center", paddingY: "40px" }}>
                  <Spinner />
                </Box>
              ) : loadError ? (
                <Box sx={{ padding: "30px", textAlign: "center" }}>
                  <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.amber}>
                    Could not load the sites for this plan. Try again in a moment.
                  </Subtext>
                </Box>
              ) : rows.length === 0 ? (
                <Box sx={{ padding: "36px", textAlign: "center" }}>
                  <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                    {search || statusFilter ? "No locations match this view." : "No locations on this plan yet."}
                  </Subtext>
                </Box>
              ) : (
                rows.map((s, idx) => {
                  const status = clientSiteStatus(s);
                  const name = s.lcpName || s.address?.line1 || "Site";
                  // Row opens the LOCATION page (/locations/:id), which
                  // lists ALL work orders at the site. Previously this
                  // deep-linked to s.latestWorkOrderId, so a site with
                  // several work orders silently opened just one of them
                  // (the bug Jack caught). The location page is the client
                  // view (role-branched in Locations/detail.tsx) and is
                  // reachable here for ANY client with a plan, whether or
                  // not the mulch calculator is enabled for their org.
                  const locId = s.locationId || null;
                  const clickable = !!locId;
                  /* Street line for the row. Suppressed when the site has
                     no name of its own and `name` already fell back to
                     the address — printing it twice helps nobody. The
                     payload sometimes hands `address` back as a raw
                     string rather than an object, so both are handled. */
                  const rawAddr: any = s.address;
                  const line1 =
                    typeof rawAddr === "string" ? rawAddr : rawAddr?.line1 || "";
                  const addrLine1 = line1 && line1 !== name ? line1 : "";
                  return (
                    <Flex
                      key={s.lcpId || idx}
                      onClick={clickable ? () => navigate(`/locations/${locId}`) : undefined}
                      sx={{
                        alignItems: "center",
                        gap: "10px",
                        padding: "11px 16px",
                        cursor: clickable ? "pointer" : "default",
                        borderBottom: idx === rows.length - 1 ? "none" : "0.5px solid " + mnt.divider,
                        "&:hover": clickable ? { background: mnt.bgSubtle } : undefined,
                      }}
                    >
                      <Box sx={{ flex: "0 0 24%", minWidth: 0 }}>
                        <Span fontFamily={"Poppins"} fontSize={"12.5px"} sx={{ color: clickable ? mnt.link : mnt.ink, fontWeight: 500 }}>
                          {name}
                        </Span>
                        {/* The ADDRESS, under the name. A client does not
                            think of their sites as "Learning Care - 4" —
                            that is our label for their record. The street
                            is how they know which playground this is. */}
                        {addrLine1 && (
                          <Box
                            as="div"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              color: mnt.textMuted,
                              marginTop: "2px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {addrLine1}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: "0 0 14%", minWidth: 0 }}>
                        <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                          {s.city || "-"}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 13%", minWidth: 0 }}>
                        <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
                          {s.lastServicedDate ? fmtDate(s.lastServicedDate) : "-"}
                        </Span>
                      </Box>
                      <Box sx={{ flex: "0 0 14%", minWidth: 0 }}>
                        <Box sx={{ ...mntPill(status.tone), fontSize: "10px" }}>{status.label}</Box>
                      </Box>
                      {/* PER-ROW REQUEST (Session 76, Jack's call).

                          Mirrors the operator roster's black "Create work
                          order" button. A row that says "Not scheduled"
                          and offers nothing is a dead end: the client has
                          to scroll back up, open the modal, then search
                          for the site they were already looking at.

                          Only on rows with nothing raised. A site that
                          already has an open work order gets no button —
                          that is the duplicate-request case deferred in
                          CLIENT_SELF_SERVICE_BUILD §8, and offering the
                          action there would invite exactly the double
                          request that section is about.

                          stopPropagation because the row itself navigates
                          to the location page. */}
                      <Box sx={{ flex: "1 1 auto", minWidth: "190px", textAlign: "right" }}>
                        {plan?.clientSelfServiceEnabled && status.key === "none" && locId && (
                          <Flex sx={{ gap: "6px", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                openRequest(
                                  {
                                    locationId: locId,
                                    name,
                                    city: s.city || "",
                                    onPlan: true,
                                    planName: plan?.name,
                                  },
                                  "request",
                                );
                              }}
                              style={{
                                background: "transparent",
                                border: "1px solid " + mnt.divider,
                                borderRadius: "4px",
                                padding: "4px 9px",
                                fontFamily: "Poppins",
                                fontSize: "10.5px",
                                color: mnt.ink,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Request work
                            </button>
                            {/* The priced path. Secondary in weight but
                                present on every actionable row: a client
                                who knows the yardage should not have to
                                open the other form to say so. */}
                            <button
                              type="button"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                openRequest(
                                  {
                                    locationId: locId,
                                    name,
                                    city: s.city || "",
                                    onPlan: true,
                                    planName: plan?.name,
                                  },
                                  "order",
                                );
                              }}
                              style={{
                                background: mnt.ink,
                                border: "none",
                                borderRadius: "4px",
                                padding: "4px 9px",
                                fontFamily: "Poppins",
                                fontSize: "10.5px",
                                color: mnt.cardBg,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Create work order
                            </button>
                          </Flex>
                        )}
                      </Box>
                    </Flex>
                  );
                })
              )}
            </Box>

            {/* Pagination */}
            <Flex sx={{ marginTop: "10px", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                {total === 0
                  ? "No locations"
                  : "Showing " +
                    ((page - 1) * PAGE_SIZE + 1).toLocaleString() +
                    " to " +
                    Math.min(page * PAGE_SIZE, total).toLocaleString() +
                    " of " +
                    total.toLocaleString()}
              </Subtext>
              {pageCount > 1 && (
                <Flex sx={{ alignItems: "center", gap: "6px" }}>
                  <PageButton label={"\u2039"} disabled={page <= 1} onClick={() => setPage(page - 1)} />
                  <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                    Page {page} of {pageCount}
                  </Subtext>
                  <PageButton label={"\u203A"} disabled={page >= pageCount} onClick={() => setPage(page + 1)} />
                </Flex>
              )}
            </Flex>
          </Box>
          </>
          )}

          {/* Corrigo notice */}
          {corrigoManaged && (
            <Box sx={{ ...mntCard, padding: "14px 18px", marginTop: "18px", background: mnt.bgBeige }}>
              <Box sx={{ ...mntType.labelSm, fontWeight: 600, marginBottom: "4px" }}>Notification preferences</Box>
              <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                Your account is set to Corrigo-managed. Proactive emails are off; approvals and
                updates continue in CorrigoPro. To adjust, contact your GetMulch account team.
              </Subtext>
            </Box>
          )}
        </Box>
      </Page>

      {requestOpen && (
        <RequestWorkModal
          planId={id}
          planName={plan?.name}
          presetSite={requestPreset}
          mode={requestMode}
          onClose={() => {
            setRequestOpen(false);
            setRequestPreset(null);
          }}
          onCreated={() => {
            // Refresh both lists rather than optimistically inserting a
            // row: the server decides whether the request landed ON the
            // plan, and the work order carries a derived stage this page
            // has no way to compute locally. A re-read is the only way
            // the table can be right.
            loadWorkOrders();
            loadSites();
            setRequestPreset(null);
          }}
        />
      )}
    </>
  );
}

function StatCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <Box sx={{ ...mntStatCard(), padding: "12px 14px" }}>
      <Subtext fontSize={"10.5px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={mnt.textMuted} sx={{ letterSpacing: "0.02em" }}>
        {label.toUpperCase()}
      </Subtext>
      <H5 fontFamily="Poppins" fontWeight={500} fontSize={"20px"} margin={0} marginTop={"4px"} sx={{ lineHeight: 1.2 }}>
        {value}
      </H5>
      {sub != null && (
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} marginTop={"2px"} color={mnt.textMuted}>
          {sub}
        </Subtext>
      )}
    </Box>
  );
}

/**
 * Client-safe site status. Mirrors the OPERATOR roster's statusPill
 * (PlanLocationsTable) exactly, just relabeled for a customer, so the
 * client status can never disagree with the internal one:
 *   - cadence LEADS when the plan has a real schedule
 *     (overdue / due_soon / current);
 *   - otherwise the OPERATIONAL status is the only honest signal
 *     (wo_received / in_progress / serviced / dormant).
 * Critically, a site with NOTHING (no cadence, no WO, never serviced)
 * reads "Not scheduled" — NOT a green "Current", which wrongly implied
 * the site was up to date when nothing had happened (the bug Jack caught).
 */
function clientSiteStatus(s: any): { key: string; label: string; tone: MntStatus } {
  // Cadence axis first (only meaningful when the plan has a cadence).
  if (s.cadenceStatus === "overdue") return { key: "overdue", label: "Overdue", tone: "overdue" };
  if (s.cadenceStatus === "due_soon") return { key: "scheduled", label: "Due soon", tone: "due" };
  if (s.cadenceStatus === "current") return { key: "current", label: "Current", tone: "current" };

  // No cadence: fall back to what the site actually shows operationally.
  switch (s.status) {
    case "wo_received":
      return { key: "quoted", label: "Work order open", tone: "woReceived" };
    case "in_progress":
      return { key: "scheduled", label: "In progress", tone: "scheduled" };
    case "serviced":
      return { key: "current", label: "Serviced", tone: "current" };
    default:
      // dormant — nothing raised, never serviced, no schedule.
      return { key: "none", label: "Not scheduled", tone: "ungrouped" };
  }
}

function PageButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      onClick={disabled ? undefined : onClick}
      sx={{
        background: mnt.cardBg,
        border: "0.5px solid " + mnt.divider,
        borderRadius: "4px",
        padding: "3px 10px",
        fontFamily: "Poppins",
        fontSize: "12px",
        color: disabled ? mnt.textFaint : mnt.ink,
        cursor: disabled ? "default" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {label}
    </Box>
  );
}

function formatYards(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  if (v % 1 === 0) return String(v);
  return v.toFixed(1);
}

function fmtDate(d: any): string {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
